import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Lead, LOAN_STATUS_CONFIG, LOAN_TYPE_LABELS, getLegacyCustomerName } from "@/types";
import { getUserName } from "@/data/mockData";
import { Phone, MessageCircle, ChevronRight, Building2, Bell } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

interface LeadCardProps {
  lead: Lead;
  pendingReminders?: number;
}

function formatCurrency(amount: number) {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(0)}L`;
  return `₹${amount.toLocaleString("en-IN")}`;
}

export function LeadCard({ lead, pendingReminders = 0 }: LeadCardProps) {
  const navigate = useNavigate();
  const statusConfig = LOAN_STATUS_CONFIG[lead.status] || LOAN_STATUS_CONFIG.new_lead;
  const badgeVariant = statusConfig.badgeVariant as any;
  const customerName = getLegacyCustomerName(lead);
  
  // Check if today's daily update exists
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const hasTodaysDailyUpdate = lead.timeline.some(item => {
    if (!item.isDailyUpdate) return false;
    const itemDate = new Date(item.createdAt);
    itemDate.setHours(0, 0, 0, 0);
    return itemDate.getTime() === today.getTime();
  });

  // Determine if the lead should show update indicator (not for closed statuses)
  const showUpdateIndicator = !['partial_disbursed', 'disbursed', 'otc', 'payout_released', 'closed', 'removed', 'followup_later'].includes(lead.status);

  const handleCall = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(`tel:${lead.mobile}`);
  };

  const handleWhatsApp = (e: React.MouseEvent) => {
    e.stopPropagation();
    const n = (lead.mobile || '').replace(/\D/g, '');
    if (n.length < 10) return; // bail out — no valid number to open
    window.open(`https://wa.me/${n.startsWith('91') && n.length > 10 ? n : '91' + n}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01 }}
      transition={{ duration: 0.2 }}
    >
      <Card
        variant="interactive"
        className="overflow-hidden"
        onClick={() => navigate(`/leads/${lead.id}`)}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Avatar + Reminders */}
            <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent font-semibold text-sm">
                {(customerName || '?').charAt(0).toUpperCase()}
              </div>
              {pendingReminders > 0 && (
                <div className="flex items-center gap-1 text-xs text-orange-500">
                  <Bell className="h-3.5 w-3.5" />
                  <span className="font-semibold">{pendingReminders}</span>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 space-y-2">
              {/* Header */}
              <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                      {/* Status Indicator Dot */}
                      {showUpdateIndicator && (
                        <div className="relative flex-shrink-0">
                          {hasTodaysDailyUpdate ? (
                            // Green dot for update submitted
                            <div className="h-2 w-2 rounded-full bg-success" />
                          ) : (
                            // Red glowing dot for update not submitted
                            <>
                              <div className="h-2 w-2 rounded-full bg-destructive animate-pulse" />
                              <div className="absolute inset-0 h-2 w-2 rounded-full bg-destructive animate-ping opacity-75" />
                            </>
                          )}
                        </div>
                      )}
                      <h3 className="font-semibold truncate">{customerName}</h3>
                    </div>
                    <p className="text-xs text-muted-foreground whitespace-nowrap">
                      {lead.isNRI
                        ? (lead.nriCountry ? `🌍 ${lead.nriCountry} (NRI) • ` : 'NRI • ')
                        : (lead.customerCity ? `${lead.customerCity} • ` : '')}{format(lead.createdAt, 'd MMM yyyy')} • {getUserName(lead.assignedTo || '')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* Property Identified tag for home_loan and LAP */}
                    {(lead.loanType === 'home_loan' || lead.loanType === 'lap') && (
                      <Badge
                        variant="outline"
                        className={lead.isPropertyIdentified
                          ? 'text-[9px] px-1 py-0 border-green-500 text-green-700 bg-green-50'
                          : 'text-[9px] px-1 py-0 border-orange-400 text-orange-600 bg-orange-50'}
                      >
                        {lead.isPropertyIdentified ? 'PI' : 'PNI'}
                      </Badge>
                    )}
                    <Badge variant={badgeVariant} className="flex-shrink-0">
                      {statusConfig.label}
                    </Badge>
                  </div>
              </div>

              {/* Details */}
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {lead.loanAmount && (
                  <>
                    <span className="font-semibold text-foreground">
                      {formatCurrency(lead.loanAmount)}
                    </span>
                    <span className="text-muted-foreground">•</span>
                  </>
                )}
                {lead.loanType && (
                  <>
                    <span className="text-muted-foreground">
                      {LOAN_TYPE_LABELS[lead.loanType]}
                    </span>
                    <span className="text-muted-foreground">•</span>
                  </>
                )}
                {lead.lender && (
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Building2 className="h-3 w-3" />
                    {lead.lender}
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-1">
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-8 px-3"
                  onClick={handleCall}
                >
                  <Phone className="h-3.5 w-3.5 mr-1" />
                  Call
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  className="h-8 px-3"
                  onClick={handleWhatsApp}
                >
                  <MessageCircle className="h-3.5 w-3.5 mr-1" />
                  WhatsApp
                </Button>
                <div className="flex-1" />
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
